/**
 * The flak-burst one-shot voice pool: one `FLAK_BURST_CUE` play per shell that goes off near the
 * player, distance-attenuated (`continuous-params.ts`'s `flakBurstGain`) and capped at
 * `FLAK_BURST_MAX_CONCURRENT` in-flight instances — a saturated engagement (several batteries,
 * five slots each) can report more bursts in one frame than are worth spending voices on, so the
 * nearest (loudest) ones win the budget, silently: at zero gain a burst outside
 * `FLAK_BURST_AUDIBLE_RANGE_M` is dropped before it can spend a concurrency slot at all, not
 * played inaudibly.
 *
 * Split out of `engine.ts`'s `FlightAudioEngineImpl` purely to keep that file under the project's
 * line-count gate — the pool still plays through the caller's own `AudioContext`/cache/bus, it's
 * just handed those once at construction instead of reaching into a private field.
 */

import { FLAK_BURST_MAX_CONCURRENT, flakBurstGain } from "./continuous-params.ts";
import { playOneShot } from "./playback.ts";
import { FLAK_BURST_CUE } from "./sfx-map.ts";
import type { BufferCache } from "./buffer-cache.ts";

export interface FlakBurstVoicePool {
  /** Distances in metres from the player to each burst reported this frame — see `ContinuousFrameParams.flakBurstDistancesM`. */
  trigger(distancesM: readonly number[] | undefined): void;
}

export function createFlakBurstVoicePool(
  context: AudioContext,
  cache: BufferCache<AudioBuffer>,
  bus: GainNode,
  isDisposed: () => boolean,
): FlakBurstVoicePool {
  let active = 0;
  return {
    trigger(distancesM) {
      if (!distancesM || distancesM.length === 0) return;
      // Nearest first, so a crowded volley spends its concurrency budget on the bursts most worth hearing.
      const sorted = [...distancesM].sort((a, b) => a - b);
      for (const distanceM of sorted) {
        if (active >= FLAK_BURST_MAX_CONCURRENT) break;
        const gain = flakBurstGain(distanceM);
        if (gain <= 0) continue;
        active++;
        playOneShot(context, cache, FLAK_BURST_CUE, bus, 1, isDisposed, gain, () => {
          active = Math.max(0, active - 1);
        });
      }
    },
  };
}
